import React, { useState, useEffect } from 'react';
import { Button, message } from 'antd';
import { DisconnectOutlined } from '@ant-design/icons';
import { useAppSelector } from '@/store/hooks/useAppSelector';
import { getLoggedInUser, getSelectedAgent, getSelectedAgentId } from '@/store/account/selector';
import { ECalendar } from 'src/enums/ECalendar';
import {
	checkGoHighLevelConnection,
	createGoHighLevelOAuthState,
	disconnectGoHighLevel,
	fetchGhlCalendars,
	getGoHighLevelAuthUrl,
	openOAuthWindow,
} from '@/pages/ReceptionistDashboard/ApiWorkflowTemplate/GoHighLevelIntegration';
import { IGhlConnectionStatusProps } from '@/interfaces/ICalendarWorkflows';

export const GhlConnectionStatus: React.FC<IGhlConnectionStatusProps> = ({ calendar }) => {
	const selectedAgent = useAppSelector(getSelectedAgent);
	const selectedAgentId = useAppSelector(getSelectedAgentId);
	const loggedInUser = useAppSelector(getLoggedInUser);
	const [isConnected, setIsConnected] = useState(false);

	const redirectURL = import.meta.env.VITE_APP_API_BASE_URL + '/api/v1/gohighlevel/auth/callback';

	const getResellerEmailCheck = () => {
		return loggedInUser.resellerId ? 'RESELLER' : 'RECEPTIONIST';
	};
	const resellerEmailCheck = getResellerEmailCheck();

	const checkConnection = async () => {
		if (calendar.nameOfCalendar !== ECalendar.GOHIGHLEVEL_CALENDAR) {
			return;
		}

		try {
			const connectionData = await checkGoHighLevelConnection({
				resellerEmailCheck,
				agentId: selectedAgentId,
			});
			const isConnected = connectionData?.isConnected || false;
			if (isConnected) {
				setIsConnected(isConnected);
				await fetchGhlCalendars({ resellerEmailCheck, agentId: selectedAgentId });
				return isConnected;
			} else {
				setIsConnected(false);
				return false;
			}
		} catch {
			setIsConnected(false);
			return false;
		}
	};

	useEffect(() => {
		if (
			calendar.nameOfCalendar === ECalendar.GOHIGHLEVEL_CALENDAR &&
			selectedAgentId &&
			resellerEmailCheck
		) {
			checkConnection();
		}
	}, [calendar.nameOfCalendar, selectedAgentId, resellerEmailCheck]);

	const handleGhlConnect = async () => {
		if (!selectedAgent?.number || !resellerEmailCheck) {
			return;
		}
		try {
			const currentPath = window.location.pathname;
			const searchParams = new URLSearchParams(window.location.search);
			searchParams.set('integration', 'gohighlevel');

			const state = createGoHighLevelOAuthState(
				selectedAgent?.number,
				resellerEmailCheck,
				currentPath,
				searchParams,
			);

			const authUrl = await getGoHighLevelAuthUrl(
				selectedAgent?.number,
				resellerEmailCheck,
				redirectURL,
				state,
			);

			openOAuthWindow(authUrl, true, 'gohighlevel');
		} catch {
			message.error('Failed to connect to GoHighLevel. Please try again.');
		}
	};

	const handleGoHighLevelDisconnect = async () => {
		try {
			await disconnectGoHighLevel(selectedAgent?.number || '', selectedAgentId || '');
			message.success('Successfully disconnected from GoHighLevel');
			setIsConnected(false);
		} catch {
			message.error('Failed to disconnect from GoHighLevel');
		}
	};

	if (calendar.nameOfCalendar !== ECalendar.GOHIGHLEVEL_CALENDAR) {
		return null;
	}

	return (
		<div className="flex items-center mr-2 -mt-1 gap-2">
			{isConnected ? (
				<>
					<span className="text-green-600 text-sm font-medium">GoHighLevel is Connected!</span>
					<Button
						danger
						size="small"
						icon={<DisconnectOutlined />}
						onClick={(e) => {
							e.stopPropagation();
							handleGoHighLevelDisconnect();
						}}
						className="mt-1"
					>
						Disconnect
					</Button>
				</>
			) : (
				<>
					<span className="text-red-500 text-sm font-medium">Not Connected</span>
					<Button
						type="primary"
						size="small"
						onClick={async () => {
							await handleGhlConnect();

							message.loading({
								content: 'Connecting to GoHighLevel...',
								key: 'ghlConnect',
								duration: 0,
							});
							const checkInterval = setInterval(async () => {
								const result = await checkConnection();
								if (result) {
									clearInterval(checkInterval);
									message.success({
										content: 'Connected to GoHighLevel successfully!',
										key: 'ghlConnect',
										duration: 2,
									});
								}
							}, 2000);
						}}
						className="mt-1"
					>
						Connect
					</Button>
				</>
			)}
		</div>
	);
};
